// Google Maps Places integration for the Address input.
//
// Uses the classic Maps JS loader (synchronous library inclusion via
// `&libraries=places`). After load, `google.maps.places.Autocomplete` is
// available immediately — no bootstrap-loader snippet required. The
// `loading=async` flag was tried earlier but it only initializes
// `google.maps.importLibrary` when loaded via Google's bootstrap-loader inline
// snippet, not a plain <script> tag.
//
// Gracefully degrades when the API key isn't configured: the input stays a
// plain text field and the helper text reflects the state.

import { useEffect, useRef, useState } from "react";

let scriptPromise: Promise<typeof google | null> | null = null;

function loadMaps(apiKey: string): Promise<typeof google | null> {
  if (scriptPromise) return scriptPromise;
  if (typeof window === "undefined") return Promise.resolve(null);

  scriptPromise = new Promise((resolve) => {
    const w = window as unknown as { google?: typeof google };
    if (w.google?.maps?.places) {
      resolve(w.google);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.async = true;
    script.onerror = () => {
      console.error("[places] Google Maps script failed to load");
      scriptPromise = null; // allow retry
      resolve(null);
    };
    script.onload = () => {
      const g = (window as unknown as { google?: typeof google }).google;
      resolve(g ?? null);
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export type PlacesStatus =
  | "idle"
  | "no-key"
  | "loading"
  | "ready"
  | "selected"
  | "error";

export interface PlacesSelection {
  formatted: string;
  houseNumber?: string;
  streetName?: string;
  borough?: string;
}

// The result of a programmatic verification: the hydrated first candidate plus
// EVERY distinct address Google returned. The candidate list is what lets the
// caller detect ambiguity — "1056 42nd St" matches both a Brooklyn 42nd Street
// and a Manhattan W 42nd St — instead of blindly trusting the first hit.
export interface PlacesVerification {
  best: PlacesSelection | null;
  /** Distinct formatted addresses Google returned (>= 1 when best != null). */
  candidates: string[];
}

// What the caller should do with a verification result:
//   "apply"   — a single result whose street matches the query: a safe
//               canonicalization ("1056 42nd street" -> "1056 42nd St,
//               Brooklyn, NY 11219"). Overwrite silently.
//   "confirm" — more than one candidate, OR the single result is a DIFFERENT
//               street than asked (Google added a "W"/"E" or picked another
//               street). Do NOT overwrite — surface the candidates and let the
//               user pick. Silently accepting this is how a correct address
//               gets replaced with the wrong borough's street.
//   "none"    — nothing usable came back; leave the address untouched.
export type VerifyDecision = "apply" | "confirm" | "none";

export function useGoogleMapsAutocomplete(
  inputRef: React.RefObject<HTMLInputElement>,
  apiKey: string | null | undefined,
  onSelect: (sel: PlacesSelection) => void,
) {
  const [status, setStatus] = useState<PlacesStatus>("idle");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!apiKey) {
      setStatus("no-key");
      return;
    }
    if (!inputRef.current) return;

    let cancelled = false;
    setStatus("loading");

    loadMaps(apiKey).then((g) => {
      if (cancelled || !inputRef.current) return;
      if (!g?.maps?.places?.Autocomplete) {
        console.error(
          "[places] Script resolved but google.maps.places.Autocomplete is missing",
        );
        setStatus("error");
        return;
      }
      try {
        const ac = new g.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "formatted_address", "geometry"],
          types: ["address"],
          componentRestrictions: { country: "us" },
        });
        autocompleteRef.current = ac;
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          const parts = parsePlace(place);
          if (parts) {
            setStatus("selected");
            onSelect(parts);
          }
        });
        setStatus("ready");
      } catch (err) {
        console.error("[places] Autocomplete init failed:", err);
        setStatus("error");
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, inputRef.current]);

  return status;
}

// Programmatic verification — given a free-text query like "1445 Fulton",
// looks up matching Places and returns EVERY distinct candidate plus a hydrated
// (address_components) best guess. Mirrors Blazor's auto-resolve on Review, but
// surfaces ambiguity instead of hiding it behind the first hit.
//
// `borough` (when known) biases an under-specified query to the RIGHT part of
// the city. Without it we fall back to ", NY" (the state) — deliberately NOT
// ", New York", which Google reads as New York County = Manhattan and which is
// exactly what silently turned a Brooklyn "42nd Street" into Manhattan's
// "W 42nd St".
export async function verifyAddressViaPlaces(
  query: string,
  apiKey: string,
  borough?: string | null,
): Promise<PlacesVerification | null> {
  const trimmed = query.trim();
  if (!trimmed) return null;
  const g = await loadMaps(apiKey);
  if (!g?.maps?.places?.PlacesService) {
    console.warn("[places] verify: PlacesService unavailable");
    return null;
  }
  const hasBoroughWord =
    /new york|nyc|brooklyn|queens|bronx|manhattan|staten/i.test(trimmed);
  const biased = hasBoroughWord
    ? trimmed
    : borough?.trim()
      ? `${trimmed}, ${borough.trim()}`
      : `${trimmed}, NY`;

  // PlacesService requires an attribution container.
  const attr = document.createElement("div");
  const service = new g.maps.places.PlacesService(attr);
  return new Promise<PlacesVerification | null>((resolve) => {
    service.findPlaceFromQuery(
      {
        query: biased,
        // findPlaceFromQuery does NOT support address_components — only the
        // basic-data fields. We grab place_id here, then hydrate components
        // via getDetails below.
        fields: ["place_id", "formatted_address", "geometry"],
      },
      (results, status) => {
        if (status !== "OK" || !results?.length) {
          resolve(null);
          return;
        }
        const candidates = dedupAddresses(
          results
            .map((r) => r.formatted_address)
            .filter((a): a is string => !!a),
        );
        // Only the first candidate is hydrated (address_components), so
        // ambiguity costs one getDetails, not one per candidate.
        const finish = (best: PlacesSelection | null) =>
          resolve({ best, candidates });
        const placeId = results[0].place_id;
        if (!placeId) {
          finish(parsePlace(results[0]));
          return;
        }
        service.getDetails(
          {
            placeId,
            fields: ["address_components", "formatted_address", "geometry"],
          },
          (detail, detailStatus) =>
            finish(
              detailStatus === "OK" && detail
                ? parsePlace(detail)
                : parsePlace(results[0]),
            ),
        );
      },
    );
  });
}

// ---------------------------------------------------------------------------
// Verification decision — PURE (no Google), so it carries the unit tests. This
// is the logic that decides whether a Places result is trustworthy enough to
// apply silently, or ambiguous enough that the user must confirm.
// ---------------------------------------------------------------------------

const DIRECTIONALS: Record<string, string> = {
  north: "n", south: "s", east: "e", west: "w",
  northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
};

// Words that name a street's TYPE, not its identity — dropped before comparing
// so "42nd Street" and "42nd St" match.
const STREET_TYPES = new Set([
  "st", "street", "ave", "avenue", "av", "rd", "road", "blvd", "boulevard",
  "pl", "place", "dr", "drive", "ln", "lane", "ct", "court", "ter", "terrace",
  "pkwy", "parkway", "hwy", "highway", "way", "cir", "circle", "sq", "square",
  "plz", "plaza", "aly", "alley", "walk", "path", "row", "loop",
]);

// Identity tokens of a street: lowercase, punctuation-stripped, directionals
// normalized to single letters (west -> w), street-type words removed. What's
// left is the discriminating part — "42nd", "fulton", plus any real directional.
function streetTokens(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => DIRECTIONALS[t] ?? t)
    .filter((t) => !STREET_TYPES.has(t));
}

// Is the returned street the SAME street the query named — a canonicalization —
// rather than a substitution? True only when every identity token of the
// returned route also appears in the query. A returned "W 42nd St" for a query
// that never said "W" fails (extra "w" token) — the exact bug we're guarding.
// An absent route can't be confirmed, so it fails closed.
export function isSameStreet(
  input: string,
  streetName: string | undefined | null,
): boolean {
  if (!streetName) return false;
  const routeTokens = streetTokens(streetName);
  if (routeTokens.length === 0) return false;
  const inputSet = new Set(streetTokens(input));
  return routeTokens.every((t) => inputSet.has(t));
}

export function decideVerification(
  input: string,
  v: PlacesVerification | null,
): VerifyDecision {
  if (!v?.best?.formatted) return "none";
  if (v.candidates.length > 1) return "confirm"; // genuinely ambiguous
  if (!isSameStreet(input, v.best.streetName)) return "confirm"; // substitution
  return "apply";
}

// Case-insensitive address dedup, ignoring a trailing ", USA" and whitespace,
// so Google's near-duplicate spellings collapse to one candidate. Exported so
// callers can merge Places candidates with the user's current address without
// showing the same address twice.
export function dedupAddresses(list: string[]): string[] {
  const key = (s: string) =>
    s.toLowerCase().replace(/,?\s*usa$/i, "").replace(/\s+/g, " ").trim();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of list) {
    const t = a.trim();
    if (!t) continue;
    const k = key(t);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(t);
    }
  }
  return out;
}

function parsePlace(
  place: google.maps.places.PlaceResult | null,
): PlacesSelection | null {
  if (!place?.address_components) return null;
  const get = (type: string, short = false) =>
    place.address_components?.find((c) => c.types.includes(type))?.[
      short ? "short_name" : "long_name"
    ];

  const streetNumber = get("street_number");
  const route = get("route");
  const borough =
    get("sublocality_level_1") ?? get("sublocality") ?? get("locality");

  return {
    formatted: place.formatted_address ?? "",
    houseNumber: streetNumber ?? undefined,
    streetName: route ?? undefined,
    borough: borough ?? undefined,
  };
}
